import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Share2 } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { Capacitor } from "@capacitor/core";
import Gnavbar from "../components/Gnavbar";
import BottomNavbar from "../components/BottomNavbar";
import MatchesContainer from "../components/MatchesContainer";
import { getRequest } from "../helpers/api.helper";
import { useMatchEndpoint } from "../hooks/useMatchEndpoint";
import { handleShortList, handleViewProfile } from "../helpers/matches.helper";
import {
  buildProfilePublicUrl,
  buildProfileShareText,
} from "../helpers/profileExport.helper";
import {
  buildSuitableMatchMessage,
  normalizeWhatsAppNumber,
  openWhatsAppChatDirect,
} from "../helpers/whatsapp.helper";
import { openNativeWhatsAppChat } from "../helpers/nativeShare";
import { MatchListEntry } from "../types";
import { useSelector } from "react-redux";
import { RootState } from "../store";

const noop = () => {};

// Lists profiles matching the SELECTED profile's partner preferences.
const SuitableProfiles = () => {
  const { profileId = "" } = useParams();
  const navigate = useNavigate();
  const bureauInfo = useSelector((state: RootState) => state.bureau.data);
  const [selectedProfile, setSelectedProfile] = useState<Record<
    string,
    any
  > | null>(null);

  // Height of the fixed top navbar (includes its safe-area padding), used as
  // the sticky offset for the title/toggle row.
  const navRef = useRef<HTMLDivElement | null>(null);
  const [navHeight, setNavHeight] = useState(0);
  useLayoutEffect(() => {
    const element = navRef.current;
    if (!element) return;
    const update = () => setNavHeight(element.getBoundingClientRect().height);
    update();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", update);
      return () => window.removeEventListener("resize", update);
    }
    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const encodedId = encodeURIComponent(profileId);
  const {
    matches,
    setMatches,
    loading,
    loadingMore,
    error,
    total,
    hasMore,
    refetch,
    loadMore,
  } = useMatchEndpoint(`/bureau/profile/${encodedId}/suitable-matches`, {
    pageSize: 10,
  });

  // The page is reused when only :profileId changes, so clear the previous
  // selected profile and ignore responses for a profile no longer in the URL.
  useEffect(() => {
    setSelectedProfile(null);
    if (!profileId) return;
    let active = true;
    getRequest(`/bureau/profile/${encodedId}`).then((response) => {
      if (active && response?.success) setSelectedProfile(response.data);
    });
    return () => {
      active = false;
    };
  }, [profileId, encodedId]);

  // Original profile = the one Suitable Profiles was opened for (route
  // :profileId). It belongs to this bureau (enforced by the backend).
  const getOriginalProfile = () =>
    selectedProfile && String(selectedProfile.martialId) === profileId
      ? selectedProfile
      : null;

  // Recipient = original profile; sender + public link = clicked match.
  const handleSendMessage = async (match: MatchListEntry) => {
    const originalProfile = getOriginalProfile();

    if (!originalProfile) {
      toast.error("Selected profile is still loading. Please try again.");
      return;
    }

    if (match.maritalId === originalProfile.martialId) {
      return;
    }

    // Matching profile public URL
    if (!match.bureauId || !match.bureauName) {
      toast.error("Public profile link is unavailable for this profile.");
      return;
    }

    const profileUrl = buildProfilePublicUrl({
      bureauId: match.bureauId,
      bureauName: match.bureauName,
      martialId: match.maritalId,
    });

    if (!profileUrl) {
      toast.error("Public profile link is unavailable for this profile.");
      return;
    }

    // Original profile is WhatsApp recipient
    const phoneNumber = normalizeWhatsAppNumber(
      originalProfile.mobileNumber,
      originalProfile.countryCode,
    );

    if (!phoneNumber) {
      toast.error("A valid phone number is missing for this profile.");
      return;
    }

    // Existing English + Telugu message
    const message = buildSuitableMatchMessage({
      bureauName: bureauInfo?.bureauName,
      recipientName: originalProfile.fullName,
      recipientGender: originalProfile.gender,
      senderName: match.fullName,
      senderGender: match.gender,
      profileUrl,
    });

    try {
      if (Capacitor.getPlatform() === "android") {
        await openNativeWhatsAppChat(phoneNumber, message);
      } else {
        openWhatsAppChatDirect(phoneNumber, message);
      }
    } catch (error) {
      console.error("WhatsApp chat failed:", error);

      toast.error("Unable to open the WhatsApp chat.");
    }
  };

  const handleShare = async (match: MatchListEntry) => {
    const originalProfile = getOriginalProfile();

    if (!originalProfile) return;

    const phoneNumber = normalizeWhatsAppNumber(
      originalProfile.mobileNumber,
      originalProfile.countryCode,
    );

    if (!phoneNumber) {
      toast.error("Original profile mobile number is unavailable.");
      return;
    }

    try {
      const message = buildProfileShareText(match, { includePublicLink: true });
      if (Capacitor.getPlatform() === "android") {
        await openNativeWhatsAppChat(phoneNumber, message);
      } else {
        openWhatsAppChatDirect(phoneNumber, message);
      }
    } catch (err) {
      console.error("Share failed:", err);
      toast.error("Unable to share profile.");
    }
  };

  const renderActions = (match: MatchListEntry) => (
    <div className="grid grid-cols-2 gap-2 w-full">
      <button
        type="button"
        disabled={!selectedProfile}
        onClick={(event) => {
          event.stopPropagation();
          handleSendMessage(match);
        }}
        className="flex min-w-0 items-center justify-center gap-2 py-2 px-3 rounded-lg bg-green-500 text-sm font-semibold text-white hover:bg-green-600 transition disabled:opacity-50"
      >
        <FaWhatsapp size={16} className="shrink-0" />
        <span className="truncate">Send Message</span>
      </button>
      <button
        type="button"
        disabled={!selectedProfile}
        onClick={(event) => {
          event.stopPropagation();
          void handleShare(match);
        }}
        className="flex min-w-0 items-center justify-center gap-2 py-2 px-3 rounded-lg border border-purple-200 text-sm font-semibold text-purple-600 hover:bg-purple-50 transition disabled:opacity-50"
      >
        <Share2 size={16} className="shrink-0" />
        <span className="truncate">Share</span>
      </button>
    </div>
  );

  const title = selectedProfile?.fullName
    ? `Suitable Profiles for ${selectedProfile.fullName}`
    : "Suitable Profiles";

  return (
    <div>
      <div ref={navRef} className="fixed top-0 left-0 right-0 z-50">
        <Gnavbar />
      </div>
      <main
        className="min-h-screen bg-gray-50 pt-15 pb-15 mt-16"
        style={
          { "--suitable-sticky-top": `${navHeight}px` } as React.CSSProperties
        }
      >
        {loading ? (
          <div className="py-12 text-center text-gray-500">
            Loading suitable profiles...
          </div>
        ) : error ? (
          <div className="max-w-md mx-auto text-center space-y-4 px-4 py-12 text-gray-600">
            <p>{error}</p>
            <button
              type="button"
              onClick={refetch}
              className="inline-flex items-center px-5 py-2 rounded-lg bg-gray-200 text-gray-700 font-semibold hover:bg-gray-300 transition"
            >
              Retry
            </button>
          </div>
        ) : (
          // Sticks MatchesContainer's header row (title, match count, List/Grid
          // toggle) directly below the fixed navbar while the cards scroll.
          <div className="[&>div>div:first-child]:sticky [&>div>div:first-child]:top-[var(--suitable-sticky-top,0px)] [&>div>div:first-child]:z-40 [&>div>div:first-child]:bg-pink-50 [&>div>div:nth-child(2)]:pt-4">
            <MatchesContainer
              title={title}
              matches={matches}
              setProfiles={setMatches}
              totalCount={total}
              hasMore={hasMore}
              loadingMore={loadingMore}
              onLoadMore={loadMore}
              onViewProfile={(match) => handleViewProfile(match, navigate)}
              onAddToShortlist={handleShortList}
              onSwitchProfile={noop}
              onShareProfile={noop}
              onReportProfile={noop}
              onManageProfile={noop}
              renderActions={renderActions}
            />
          </div>
        )}
      </main>
      <BottomNavbar />
    </div>
  );
};

export default SuitableProfiles;
